<template>
  <v-container>
    <div class="d-flex align-center flex-wrap mb-4" style="gap: 12px;">
      <v-btn :to="{ name: 'TournamentView' }" variant="outlined">Back to Tournament</v-btn>
      <v-btn :to="{ name: 'TournamentResultsList' }" variant="outlined">Tournament results</v-btn>
      <h1 class="text-h5">Linked players results</h1>
    </div>

    <p class="text-body-2 text-medium-emphasis mb-4">
      Results for players who granted you permission to add them to tournaments (session-linked), across all your saved tournament results.
    </p>

    <v-card v-if="loading" class="pa-4 text-center">Loading…</v-card>
    <v-card v-else-if="rows.length === 0" class="pa-4 text-center">
      No linked player results yet. When you save tournament results, players you added from your allowed list will appear here.
    </v-card>
    <v-card v-else>
      <v-table>
        <thead>
          <tr>
            <th>Tournament</th>
            <th>Date ended</th>
            <th>Player</th>
            <th>Place</th>
            <th>Winnings</th>
            <th>Total paid</th>
            <th>Net</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="(row, index) in rows" :key="index">
            <td>{{ row.tournamentName }}</td>
            <td>{{ formatDate(row.endedAt) }}</td>
            <td>{{ row.player.name }}</td>
            <td>{{ row.player.place }}</td>
            <td>{{ formatMoney(row.player.winnings) }}</td>
            <td>{{ formatMoney(row.player.totalPaid) }}</td>
            <td>{{ formatMoney((row.player.winnings ?? 0) - (row.player.totalPaid ?? 0)) }}</td>
          </tr>
        </tbody>
      </v-table>
    </v-card>
  </v-container>
</template>

<script setup>
import { ref, onMounted, computed } from 'vue';
import { db } from '@/firebaseConfig';
import { collection, query, where, getDocs } from 'firebase/firestore';
import { SAVED_TOURNAMENT_RESULTS_DB } from '@/constants/constants';
import { useUserStore } from '@/stores/users';

const userStore = useUserStore();
const loading = ref(true);
const results = ref([]);

const userId = computed(() => userStore.userid || userStore.user?.user_id);

const rows = computed(() => {
  const out = [];
  for (const result of results.value) {
    const players = result.players || [];
    const linkedPlayers = players.filter(
      p => p.linkedUserAlias || p.linkedUserId
    );
    for (const player of linkedPlayers) {
      out.push({
        tournamentName: result.tournamentName,
        resultId: result.id,
        endedAt: result.endedAt,
        buyInAmount: result.buyInAmount,
        totalPrizePool: result.totalPrizePool,
        player: {
          name: player.name,
          place: player.place,
          winnings: player.winnings,
          totalPaid: player.totalPaid,
          linkedUserAlias: player.linkedUserAlias
        }
      });
    }
  }
  out.sort((a, b) => (b.endedAt ?? 0) - (a.endedAt ?? 0));
  return out;
});

function formatDate(ms) {
  if (ms == null) return '—';
  return new Date(ms).toLocaleDateString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  });
}

function formatMoney(val) {
  if (val == null || Number.isNaN(Number(val))) return '—';
  return '$' + Number(val).toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 2 });
}

async function loadResults() {
  const uid = userId.value;
  if (!uid) {
    loading.value = false;
    return;
  }
  loading.value = true;
  try {
    const col = collection(db, SAVED_TOURNAMENT_RESULTS_DB);
    const q = query(col, where('createdBy', '==', uid));
    const snap = await getDocs(q);
    results.value = snap.docs.map(d => ({ id: d.id, ...d.data() }));
  } catch (e) {
    console.error('Error loading tournament results for linked players:', e);
    results.value = [];
  }
  loading.value = false;
}

onMounted(loadResults);
</script>
